import { useState, useEffect } from 'react';
import { Image, Text, Button, Modal } from '@belong/ui';
import { clsx } from 'clsx';
import { noop } from 'es-toolkit';
import { useFeatureFlag } from 'hooks/use-account-redesign';
import { AGREEMENT_FLOW_STRINGS } from 'strings/agreement-flow.strings';
import css from './belong-standards-link.module.css';
import { BelongStandardsWrapper } from './components/belong-standards-wrapper/belong-standards-wrapper';

type Props = {
  variant?: 'invitation' | 'post-inspection';
};

export function BelongStandardsLink({ variant = 'invitation' }: Props) {
  const [isBelongStandardsModalOpen, setIsBelongStandardsModalOpen] =
    useState(false);
  const { isEnabled: isBelongStandardsEnabled } =
    useFeatureFlag('belong-standards');

  const belongStandardsUrl = `${window.location.origin}/belong-standards?activeModal=BELONG_STANDARDS`;

  function handleBelongStandardsModalOpen() {
    setIsBelongStandardsModalOpen(true);
  }

  function handleBelongStandardsModalDismiss() {
    setIsBelongStandardsModalOpen(false);
  }

  useEffect(() => {
    function handleMessage(event: MessageEvent) {
      if (
        event.data?.type === 'CLOSE_BELONG_STANDARDS_MODAL' &&
        event.data?.source === 'belong-standards-iframe'
      ) {
        handleBelongStandardsModalDismiss();
      }
    }

    window.addEventListener('message', handleMessage);

    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, []);

  if (!isBelongStandardsEnabled) {
    return null;
  }

  return (
    <>
      <BelongStandardsWrapper
        className={clsx(
          'relative isolate flex w-full items-center justify-center text-center',
          variant === 'post-inspection' && css.gradientBorder,
          variant === 'post-inspection' &&
            `group relative z-10 flex-col overflow-hidden rounded bg-white p-sm before:absolute before:inset-0
             before:-z-10 before:rounded before:transition-all before:duration-hover before:ease-in-out
             before:content-[""] before:hover:opacity-0 md:flex-row md:justify-between`,
          variant === 'invitation' && 'flex-col pb-2xl',
        )}
        onClick={handleBelongStandardsModalOpen}
        variant={variant}
      >
        {variant === 'invitation' && (
          <div className="mb-xl w-full border-x-0 border-b-0 border-t border-dashed border-gray lg:hidden" />
        )}
        {variant === 'post-inspection' && (
          <div className="absolute inset-0 h-full w-full rounded border-2 border-solid border-transparent opacity-0 transition-all duration-hover group-hover:border-dark-green group-hover:opacity-100" />
        )}
        <div className="contents lg:flex lg:w-full lg:flex-row lg:items-center lg:justify-between">
          <div
            className={clsx(
              'contents lg:flex lg:items-center lg:gap-xs',
              variant === 'post-inspection' &&
                'md:flex md:items-center md:gap-xs',
            )}
          >
            <div
              className={clsx(
                'mb-xs h-xl w-[50px] lg:mb-0',
                variant === 'post-inspection' && 'md:mb-0',
              )}
            >
              <Image
                src="modals/belong-standards/belong_standard_badge_sm.png"
                className="object-cover"
                alt="Belong Standard Badge"
              />
            </div>
            <div
              className={clsx(
                'max-w-lg lg:my-auto lg:max-w-none',
                variant === 'invitation' && 'mb-lg',
                variant === 'post-inspection' && 'mb-xs md:mb-0 md:max-w-none',
              )}
            >
              <Text className={css.belongStandards} fontWeight="semibold">
                {variant === 'invitation' &&
                  AGREEMENT_FLOW_STRINGS.belongStandards}
                {variant === 'post-inspection' &&
                  AGREEMENT_FLOW_STRINGS.belongStandardsPostInspection}
              </Text>
            </div>
          </div>
          <div className="z-10 my-auto">
            {variant === 'invitation' && (
              <Button variant="text" onClick={handleBelongStandardsModalOpen}>
                <Text fontWeight="semibold">
                  {AGREEMENT_FLOW_STRINGS.learnMore}
                </Text>
              </Button>
            )}
            {variant === 'post-inspection' && (
              <div className="uppercase">
                <Text fontWeight="semibold">
                  {AGREEMENT_FLOW_STRINGS.learnMore}
                </Text>
              </div>
            )}
          </div>
        </div>
        <div
          className={clsx(
            'absolute z-0 h-5xl w-5xl',
            variant === 'invitation' && 'bottom-xl right-0 lg:h-6xl lg:w-6xl',
            variant === 'post-inspection' &&
              '-bottom-sm -right-xs md:h-3xl md:w-3xl',
          )}
        >
          <Image
            src="modals/belong-standards/belong_standard_badge_gray.svg"
            className="object-cover"
            alt="Belong Standard Badge"
          />
        </div>
      </BelongStandardsWrapper>
      <Modal
        isOpen={isBelongStandardsModalOpen}
        onDismiss={handleBelongStandardsModalDismiss}
        name="belong-standards-modal-iframe"
        size="fullScreen"
        childrenClassName="md:py-0"
        overlayClassName="bg-dark-navy md:bg-white-translucent overflow-y-hidden"
        closeButton={() => noop}
      >
        <div className="mx-auto flex h-full w-full flex-col">
          <iframe
            src={belongStandardsUrl}
            className="h-screen w-full border-0"
            title="Belong Standards"
            allowFullScreen
          />
        </div>
      </Modal>
    </>
  );
}
